import React, {
  FunctionComponent, useEffect, useState, Fragment,
} from 'react';
import { ConnectedProps, connect } from 'react-redux';
import { SelectionChangedEvent, ColDef } from '@ag-grid-enterprise/all-modules';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'data/root.reducers';
import { WithAuditDetails, AuditDetails } from 'data/types';
import {
  MediaGatewayPrivileges,
} from 'data/mediaGateway/mediaGateway.types';
import * as actions from 'data/mediaGateway/mediaGateway.actions';
import * as networkActions from 'data/network/network.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as selectors from 'data/mediaGateway/mediaGateway.selectors';
import * as networkSelectors from 'data/network/network.selectors';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import * as supportedFeaturesSelectors from 'data/supportedFeatures/supportedFeatures.selectors';
import { AppToaster } from 'shared/toaster';
import MediaGatewayGrid from './components/MediaGatewayGrid';
import { mediaGatewayColumnDefs } from '../../MediaGateway.types';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  beforeLogout: state.authentication.beforeLogout,
  privileges: state.authentication.privileges,
  data: selectors.getMediaGatewayList(state),
  selectedMediaGateway: selectors.getSelectedMediaGateway(state),
  networks: networkSelectors.getNetworks(state),
  deliveryFunctionId: state.global.selectedDF,
});

const mapDispatch = {
  goToMediaGatewayCreate: actions.goToMediaGatewayCreate,
  goToMediaGatewayEdit: actions.goToMediaGatewayEdit,
  goToMediaGatewayView: actions.goToMediaGatewayView,
  selectMediaGateway: actions.selectMediaGateway,
  getMediaGatewayList: actions.getMediaGatewayList,
  deleteMediaGateway: actions.deleteMediaGateway,
  getNetworks: networkActions.getNetworks,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter

export const MediaGatewayList: FunctionComponent<Props> = ({
  isAuditEnabled,
  userId,
  userName,
  history,
  beforeLogout,
  privileges,
  data,
  selectedMediaGateway,
  deliveryFunctionId,
  goToMediaGatewayCreate,
  goToMediaGatewayEdit,
  goToMediaGatewayView,
  selectMediaGateway,
  getMediaGatewayList,
  deleteMediaGateway,
  getNetworks
}: Props) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<Date>();
  const [columnDefs] = useState<ColDef[]>(mediaGatewayColumnDefs);

  useEffect(() => {
    getMediaGatewayList();
    getNetworks(deliveryFunctionId);
    return () => {
      AppToaster.clear();
    };
  }, [getMediaGatewayList, getNetworks]);

  useEffect(() => {
    setLastUpdatedAt(new Date());
  }, [data]);

  const handleRefresh = () => getMediaGatewayList();

  const handleCreateNew = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    goToMediaGatewayCreate(history);
  };

  const handleSelectionChanged = (event: SelectionChangedEvent) => {
    selectMediaGateway(event.api.getSelectedRows());
  };

  const handleDeleteMediaGateway = () => {
    const mediaGatewayId = selectedMediaGateway[0]?.id;
    if (mediaGatewayId == null) {
      return;
    }

    const body: WithAuditDetails<void> = { data: undefined };

    if (isAuditEnabled) {
      const auditDetails = getAuditDetails(
        isAuditEnabled,
        userId,
        userName,
        getAuditFieldInfo(null),
        selectedMediaGateway[0]?.name,
        AuditService.MEDIA_GATEWAY,
        AuditActionType.MEDIA_GATEWAY_DELETE,
      );
      body.auditDetails = auditDetails as AuditDetails;
    }

    deleteMediaGateway(String(mediaGatewayId), body);
  };

  const handleGoToEdit = () => goToMediaGatewayEdit(history);

  const handleGoToView = () => goToMediaGatewayView(history);

  const hasPrivilegeToView = privileges.includes(MediaGatewayPrivileges.View);
  const hasPrivilegesToCreate = privileges.includes(MediaGatewayPrivileges.Edit);
  const hasPrivilegeToDelete = privileges.includes(MediaGatewayPrivileges.Delete);

  return (
    <Fragment>
      <MediaGatewayGrid
        data={data}
        hasPrivilegeToView={hasPrivilegeToView}
        hasPrivilegesToCreate={hasPrivilegesToCreate}
        hasPrivilegeToDelete={hasPrivilegeToDelete}
        noResults={false}
        beforeLogout={beforeLogout}
        columnDefs={columnDefs}
        lastUpdatedAt={lastUpdatedAt}
        selectedMediaGateway={selectedMediaGateway[0]}
        onRefresh={handleRefresh}
        onCreateNew={handleCreateNew}
        onSelectionChanged={handleSelectionChanged}
        onDelete={handleDeleteMediaGateway}
        onEdit={handleGoToEdit}
        onView={handleGoToView}
      />
    </Fragment>
  );
};

export default withRouter(connector(MediaGatewayList));
